import { styled } from '@mui/material/styles';
import { 
  Box, 
  Button, 
  Container, 
  Card,
  TextField,
  Stepper,
  IconButton
} from '@mui/material';
import { HEALTH_GREEN } from '../utils/mockData';

// Styled components
export const PageContainer = styled(Container)(({ theme }) => ({
  padding: theme.spacing(4),
  maxWidth: '800px',
  minHeight: '100vh',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  overflowY: 'auto',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2),
    justifyContent: 'center',
    paddingBottom: theme.spacing(6),
  }
}));

export const AuthCard = styled(Card)(({ theme }) => ({
  borderRadius: 20,
  boxShadow: '0 2px 14px rgba(0,0,0,0.06)',
  overflow: 'visible',
  padding: theme.spacing(4),
  border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.03)'}`,
  backgroundColor: theme.palette.mode === 'dark' ? '#1A1A1A' : '#FFFFFF',
  maxWidth: '400px',
  width: '100%',
  margin: '0 auto',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2.5),
    borderRadius: 16,
  }
}));

export const RegisterCard = styled(Card)(({ theme }) => ({
  borderRadius: 20,
  boxShadow: '0 2px 14px rgba(0,0,0,0.06)',
  overflow: 'visible',
  padding: theme.spacing(4),
  border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.03)'}`,
  backgroundColor: theme.palette.mode === 'dark' ? '#1A1A1A' : '#FFFFFF',
  maxWidth: '600px',
  width: '100%',
  margin: '0 auto',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2.5),
    borderRadius: 16,
  }
}));

export const ActionButton = styled(Button)(({ theme }) => ({
  borderRadius: 12,
  padding: theme.spacing(1.5, 3),
  textTransform: 'none',
  fontWeight: 500,
  fontSize: '16px',
  boxShadow: 'none',
  marginTop: theme.spacing(3),
  backgroundColor: HEALTH_GREEN,
  '&:hover': {
    backgroundColor: '#3d8b40',
    boxShadow: 'none',
  },
  '&.Mui-disabled': {
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(76, 175, 80, 0.5)' : 'rgba(76, 175, 80, 0.5)',
  },
  [theme.breakpoints.down('sm')]: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(1.2, 2),
    fontSize: '14px',
  }
}));

export const BackButton = styled(Button)(({ theme }) => ({
  borderRadius: 12,
  padding: theme.spacing(1.5, 3),
  textTransform: 'none',
  fontWeight: 500,
  fontSize: '16px',
  boxShadow: 'none',
  marginTop: theme.spacing(3),
  marginRight: theme.spacing(2),
  backgroundColor: theme.palette.mode === 'dark' ? '#2C2C2E' : '#F5F5F7',
  color: theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000',
  '&:hover': {
    backgroundColor: theme.palette.mode === 'dark' ? '#3A3A3C' : '#EBEBED',
    boxShadow: 'none',
  },
  [theme.breakpoints.down('sm')]: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(1.2, 2),
    fontSize: '14px',
    marginRight: theme.spacing(1),
  }
}));

export const StyledTextField = styled(TextField)(({ theme }) => ({
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(2),
  '& .MuiOutlinedInput-root': {
    borderRadius: 12,
    backgroundColor: theme.palette.mode === 'dark' ? '#2C2C2E' : '#F5F5F7',
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: HEALTH_GREEN,
      borderWidth: '2px',
    }
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: HEALTH_GREEN,
  },
}));

export const FaceIDButton = styled(IconButton)(({ theme }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? '#2C2C2E' : '#F5F5F7',
  borderRadius: 12,
  padding: theme.spacing(1.5),
  width: '100%',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  gap: theme.spacing(1),
  marginTop: theme.spacing(2),
  border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.03)'}`,
  '&:hover': {
    backgroundColor: theme.palette.mode === 'dark' ? '#3A3A3C' : '#EBEBED',
  },
}));

export const OrDivider = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(2),
  width: '100%',
}));

export const TabButton = styled(Button)(({ theme, selected }) => ({
  borderRadius: 12,
  padding: theme.spacing(1, 2),
  textTransform: 'none',
  fontWeight: selected ? 600 : 400,
  fontSize: '14px',
  flex: 1,
  backgroundColor: selected 
    ? theme.palette.mode === 'dark' ? '#3A3A3C' : '#F5F5F7'
    : 'transparent',
  color: selected
    ? theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000'
    : theme.palette.text.secondary,
  '&:hover': {
    backgroundColor: theme.palette.mode === 'dark' ? '#3A3A3C' : '#F5F5F7',
  },
}));

export const CountryOption = styled(Box)(({ theme, selected }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(2.5),
  borderRadius: 12,
  backgroundColor: theme.palette.mode === 'dark' ? '#2C2C2E' : '#F5F5F7',
  border: selected 
    ? `2px solid ${theme.palette.mode === 'dark' ? HEALTH_GREEN : HEALTH_GREEN}`
    : `2px solid transparent`,
  marginBottom: theme.spacing(2),
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: theme.palette.mode === 'dark' ? '#3A3A3C' : '#EBEBED',
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2),
  }
}));

export const FlagContainer = styled(Box)(({ theme }) => ({
  width: 30,
  height: 20,
  borderRadius: 4,
  overflow: 'hidden',
  marginRight: theme.spacing(1),
  border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
}));

export const CustomStepper = styled(Stepper)(({ theme }) => ({
  marginBottom: theme.spacing(4),
  '& .MuiStepIcon-root': {
    color: theme.palette.mode === 'dark' ? '#3A3A3C' : '#E8E8ED',
    '&.Mui-active, &.Mui-completed': {
      color: HEALTH_GREEN,
    }
  },
  [theme.breakpoints.down('sm')]: {
    marginBottom: theme.spacing(2),
    '& .MuiStepLabel-label': {
      fontSize: '0.75rem',
    },
    '& .MuiStepIcon-root': {
      fontSize: '1.2rem',
    },
  }
}));

export const CameraViewContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  height: '300px',
  borderRadius: '12px',
  overflow: 'hidden',
  backgroundColor: theme.palette.mode === 'dark' ? '#1A1A1A' : '#F5F5F7',
  marginBottom: theme.spacing(2),
  [theme.breakpoints.down('sm')]: {
    height: '220px',
  }
}));

export const VideoElement = styled('video')({
  width: '100%',
  height: '100%',
  objectFit: 'cover',
});

export const CredentialPreview = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  height: '200px',
  borderRadius: '12px',
  backgroundColor: theme.palette.mode === 'dark' ? '#2C2C2E' : '#F5F5F7',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden',
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(2),
  [theme.breakpoints.down('sm')]: {
    height: '150px',
  }
}));

export const CredentialImage = styled('img')({
  maxWidth: '100%',
  maxHeight: '100%',
  objectFit: 'contain',
});

export const CameraControls = styled(Box)(({ theme }) => ({
  position: 'absolute',
  bottom: theme.spacing(2),
  left: 0,
  right: 0,
  display: 'flex',
  justifyContent: 'center',
  gap: theme.spacing(2),
  zIndex: 2,
}));

export const CameraButton = styled(IconButton)(({ theme }) => ({
  backgroundColor: HEALTH_GREEN,
  color: 'white',
  '&:hover': {
    backgroundColor: '#3d8b40',
  },
  padding: theme.spacing(1.5),
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1),
  }
}));

export const ScanButtonContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(2),
  borderRadius: 12,
  backgroundColor: theme.palette.mode === 'dark' ? '#2C2C2E' : '#F5F5F7',
  color: theme.palette.mode === 'dark' ? '#FFFFFF' : '#000000',
  border: `1px dashed ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)'}`,
  textTransform: 'none',
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(2),
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: theme.palette.mode === 'dark' ? '#3A3A3C' : '#EBEBED',
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1.5),
    fontSize: '0.875rem',
  }
}));